import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';

import {
  GoalSource,
  GoalsGridRequest,
  GoalsGridResponseData,
  GoalsService,
  GoalStatus,
  Pillar,
} from '../../../services/goals.service';

/**
 * Fleet-wide admin listing of Goal documents (one row per Goal), with Pause/Resume/Abandon/Force
 * Recalc row actions — the missing counterpart to the per-user drill-down (goals-panel.component.ts,
 * search-user page): this page finds WHO to act on across all users, that page acts on one already-
 * found user. Distinct from user-objectives-list.component.ts (per-objective-assignment grain,
 * Retire only) and plan-progressor-monitor.component.ts (filters by Plan-Progressor ATTENTION
 * flags, not Goal status/pillar/source). Column filters/search dropdowns mirror
 * user-objectives-list.component.ts's own nz-filter-trigger pattern exactly.
 */
@Component({
  selector: 'app-goals-list',
  templateUrl: './goals-list.component.html',
})
export class GoalsListComponent extends AnyoComponent {
  constructor(
    private goalsSvc: GoalsService,
    auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastService,
    private modal: NzModalService,
  ) {
    super(auth);
  }

  GoalStatus = GoalStatus;
  statusOptions = Object.values(GoalStatus);
  pillarOptions = Object.values(Pillar);
  sourceOptions = Object.values(GoalSource);

  tableLoading = false;
  page = 1;
  pageSize = 20;
  totalRecords = 0;
  pageSizeOptions = [20, 50, 100];
  data: GoalsGridResponseData[] = [];

  // Column search dropdowns (regex, text input + Ok/Reset).
  userSearch = '';
  partnerIdSearch = '';
  assignedExpertNameSearch = '';

  // Column filter dropdowns (checkbox multi-select).
  statusFilterValues: GoalStatus[] = [];
  heroPillarFilterValues: Pillar[] = [];
  sourceFilterValues: GoalSource[] = [];
  // Default excludes archived, matching the objectives-catalog page's own IS_ARCHIVED convention.
  isArchivedFilterValues: string[] = ['false'];

  // Date range on startDate.
  startDateFrom: Date | null = null;
  startDateTo: Date | null = null;

  // Default OFF — test users are excluded unless an admin explicitly opts in to see them.
  includeTestUsers = false;

  // Row actions — single in-flight guard, matching goals-panel.component.ts's own posture (this is
  // low-frequency admin support tooling, not a page acted on concurrently by many admins at once).
  actionLoading = false;
  isAbandonModalOpen = false;
  abandonReason = '';
  abandonTargetRow: GoalsGridResponseData | null = null;

  private fetchRequestId = 0;

  async ready() {
    if (!this.user) return;
    const restored = this.restoreQueryFromUrl();
    if (restored) this.applyRequestToState(restored);
    this.fetch();
  }

  buildRequest(exportFlag = false): GoalsGridRequest {
    const req: GoalsGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: exportFlag,
      filters: {
        ...(this.statusFilterValues.length
          ? { STATUS: this.statusFilterValues }
          : {}),
        ...(this.heroPillarFilterValues.length
          ? { HERO_PILLAR: this.heroPillarFilterValues }
          : {}),
        ...(this.sourceFilterValues.length
          ? { SOURCE: this.sourceFilterValues }
          : {}),
        IS_ARCHIVED: this.isArchivedFilterValues,
        EXCLUDE_TEST_USERS: [this.includeTestUsers ? 'false' : 'true'],
      },
    };
    const searchQuery: GoalsGridRequest['searchQuery'] = {};
    if (this.userSearch.trim()) searchQuery.USER = this.userSearch.trim();
    if (this.partnerIdSearch.trim())
      searchQuery.PARTNER_ID = this.partnerIdSearch.trim();
    if (this.assignedExpertNameSearch.trim())
      searchQuery.ASSIGNED_EXPERT_NAME = this.assignedExpertNameSearch.trim();
    if (Object.keys(searchQuery).length) req.searchQuery = searchQuery;
    if (this.startDateFrom) {
      req.dateFilters = {
        START_DATE: {
          from: this.startDateFrom.toISOString(),
          ...(this.startDateTo
            ? { to: this.startDateTo.toISOString() }
            : {}),
        },
      };
    }
    return req;
  }

  fetch(): void {
    this.tableLoading = true;
    const req = this.buildRequest(false);
    this.updateUrlWithQuery(req);
    const requestId = ++this.fetchRequestId;
    this.goalsSvc.getGoalsGrid(req).subscribe({
      next: (res) => {
        if (requestId !== this.fetchRequestId) return;
        this.data = res.data;
        this.totalRecords = res.totalRecords;
        this.pageSize = res.pageSize;
        this.tableLoading = false;
      },
      error: (err) => {
        if (requestId !== this.fetchRequestId) return;
        this.tableLoading = false;
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(
          errorBody?.description ?? 'Failed to load goals',
        );
      },
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.fetch();
  }

  clearFilters(): void {
    this.userSearch = '';
    this.partnerIdSearch = '';
    this.assignedExpertNameSearch = '';
    this.statusFilterValues = [];
    this.heroPillarFilterValues = [];
    this.sourceFilterValues = [];
    this.isArchivedFilterValues = ['false'];
    this.startDateFrom = null;
    this.startDateTo = null;
    this.includeTestUsers = false;
    this.page = 1;
    this.fetch();
  }

  nzPageIndexChange(p: number): void {
    this.page = p;
    this.fetch();
  }

  nzPageSizeChange(s: number): void {
    this.pageSize = s;
    this.fetch();
  }

  onStatusFilterChange(values: string[]): void {
    this.statusFilterValues = values as GoalStatus[];
  }

  onHeroPillarFilterChange(values: string[]): void {
    this.heroPillarFilterValues = values as Pillar[];
  }

  onSourceFilterChange(values: string[]): void {
    this.sourceFilterValues = values as GoalSource[];
  }

  onIsArchivedFilterChange(values: string[]): void {
    this.isArchivedFilterValues = values;
  }

  onDateRangeChange(range: (Date | null)[] | null): void {
    this.startDateFrom = range?.[0] ?? null;
    this.startDateTo = range?.[1] ?? null;
  }

  canPause(row: GoalsGridResponseData): boolean {
    return row.status === GoalStatus.ACTIVE;
  }

  canResume(row: GoalsGridResponseData): boolean {
    return row.status === GoalStatus.PAUSED;
  }

  canAbandon(row: GoalsGridResponseData): boolean {
    return row.status === GoalStatus.ACTIVE || row.status === GoalStatus.PAUSED;
  }

  // No status gating, unlike pause/resume/abandon — a paused or active goal can both legitimately
  // want a forced re-evaluation; only an abandoned goal has no active userObjective left to recalc.
  canForceRecalc(row: GoalsGridResponseData): boolean {
    return row.status !== GoalStatus.ABANDONED;
  }

  pauseGoal(row: GoalsGridResponseData): void {
    this.modal.confirm({
      nzTitle: 'Pause this goal?',
      nzContent: `This will pause ${row.userName || row.userEmail || 'this user'}'s current goal and freeze its tracked targets.`,
      nzOnOk: () => this.runAction(this.goalsSvc.pauseGoal(row.userId)),
    });
  }

  resumeGoal(row: GoalsGridResponseData): void {
    this.modal.confirm({
      nzTitle: 'Resume this goal?',
      nzContent: `This will re-activate ${row.userName || row.userEmail || 'this user'}'s paused goal and re-apply its targets.`,
      nzOnOk: () => this.runAction(this.goalsSvc.resumeGoal(row.userId)),
    });
  }

  openAbandonModal(row: GoalsGridResponseData): void {
    this.abandonTargetRow = row;
    this.abandonReason = '';
    this.isAbandonModalOpen = true;
  }

  confirmAbandon(): void {
    if (!this.abandonTargetRow) return;
    this.runAction(
      this.goalsSvc.abandonGoal(
        this.abandonTargetRow.userId,
        this.abandonReason || undefined,
      ),
    ).then(() => {
      this.isAbandonModalOpen = false;
      this.abandonTargetRow = null;
    });
  }

  forceRecalc(row: GoalsGridResponseData): void {
    this.modal.confirm({
      nzTitle: 'Force weekly recalc?',
      nzContent: `Runs the weekly Plan Progressor decision + recap right now for ${row.userName || row.userEmail || 'this user'}, against whatever signal data already exists — bypasses the normal 7-day/week-1 wait, but not any missing-data checks.`,
      nzOnOk: () => {
        if (this.actionLoading) return Promise.resolve();
        this.actionLoading = true;
        return new Promise<void>((resolve) => {
          this.goalsSvc.forceRecalc(row.userId).subscribe({
            next: (res) => {
              this.actionLoading = false;
              if (res.success) {
                this.toastrService.showSuccess(
                  `Recalculated ${res.userObjectivesRecalculated ?? 0} objective(s)` +
                    (res.recapGenerated ? ', recap regenerated' : ''),
                );
                this.fetch();
              } else {
                this.toastrService.showAnyoErrorToast(
                  res.error ?? 'Force recalc failed',
                );
              }
              resolve();
            },
            error: (err) => {
              this.actionLoading = false;
              const errorBody = err.error as IAnyoError;
              this.toastrService.showAnyoErrorToast(
                errorBody?.description ?? 'Force recalc failed',
              );
              resolve();
            },
          });
        });
      },
    });
  }

  /**
   * Returns a Promise so `nzOnOk` (modal.confirm) keeps its confirm dialog open with a loading
   * state until the request settles, mirroring goals-panel.component.ts's own runAction exactly.
   * Resolves to whether the action succeeded; no-ops (resolves false) if an action is already in
   * flight, guarding against overlapping duplicate requests across rows.
   */
  private runAction(
    obs: ReturnType<GoalsService['pauseGoal']>,
  ): Promise<boolean> {
    if (this.actionLoading) {
      return Promise.resolve(false);
    }
    this.actionLoading = true;
    return new Promise<boolean>((resolve) => {
      obs.subscribe({
        next: (res) => {
          this.actionLoading = false;
          if (res.success) {
            this.toastrService.showSuccess('Goal updated successfully');
            this.fetch();
          } else {
            this.toastrService.showAnyoErrorToast(res.error ?? 'Action failed');
          }
          resolve(res.success);
        },
        error: (err) => {
          this.actionLoading = false;
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(
            errorBody?.description ?? 'Action failed',
          );
          resolve(false);
        },
      });
    });
  }

  private updateUrlWithQuery(req: GoalsGridRequest): void {
    const encoded = encodeURIComponent(JSON.stringify(req));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { gridQuery: encoded },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private restoreQueryFromUrl(): GoalsGridRequest | null {
    const qp = this.route.snapshot.queryParamMap.get('gridQuery');
    if (!qp) return null;
    try {
      const parsed: unknown = JSON.parse(decodeURIComponent(qp));
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return null;
      }
      return parsed as GoalsGridRequest;
    } catch {
      return null;
    }
  }

  private applyRequestToState(req: GoalsGridRequest): void {
    if (typeof req.page === 'number') this.page = req.page;
    if (typeof req.pageSize === 'number') this.pageSize = req.pageSize;
    if (req.searchQuery?.USER) this.userSearch = req.searchQuery.USER;
    if (req.searchQuery?.PARTNER_ID)
      this.partnerIdSearch = req.searchQuery.PARTNER_ID;
    if (req.searchQuery?.ASSIGNED_EXPERT_NAME)
      this.assignedExpertNameSearch = req.searchQuery.ASSIGNED_EXPERT_NAME;
    this.statusFilterValues = (req.filters?.STATUS as GoalStatus[]) ?? [];
    this.heroPillarFilterValues =
      (req.filters?.HERO_PILLAR as Pillar[]) ?? [];
    this.sourceFilterValues = (req.filters?.SOURCE as GoalSource[]) ?? [];
    this.isArchivedFilterValues = req.filters?.IS_ARCHIVED ?? ['false'];
    this.includeTestUsers = req.filters?.EXCLUDE_TEST_USERS?.[0] === 'false';
    if (req.dateFilters?.START_DATE?.from) {
      this.startDateFrom = new Date(req.dateFilters.START_DATE.from);
    }
    if (req.dateFilters?.START_DATE?.to) {
      this.startDateTo = new Date(req.dateFilters.START_DATE.to);
    }
  }
}
